import { enUS } from 'date-fns/locale';
import { format, isValid } from 'date-fns';

export const compareWith: (o1: any, o2: any) => boolean = (a1, a2) => a1 === a2;

export function isNonEmptyString(value: any): boolean {
  return typeof value === 'string' && value !== '';
}

export const dateFormatter = (
  value: Date,
  formatStr: string,
  locale = enUS
): string => {
  if (value instanceof Date && isValid(value)) {
    return format(value, formatStr, { locale });
  } else {
    // TODO: need to write logic to handle string or number
    return '';
  }
};

export function disableBodyScroll(): void {
  document.body.style.overflow = 'hidden';
}

export function enableBodyScroll(): void {
  document.body.style.overflow = 'auto';
}

export function humanizeString(str: string): string {
  const words = str.split(/[_-]|(?=[A-Z])/);

  // Capitalize the first letter of each word and join them with spaces
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

type DebouncedFunction<T extends (...args: any[]) => any> = (...args: Parameters<T>) => void;

export function debounce<T extends (...args: any[]) => any>(func: T, delay: number): DebouncedFunction<T> {
  let timeoutId: number;

  return function debounced(...args: Parameters<T>): void {
    clearTimeout(timeoutId);

    timeoutId = window.setTimeout(() => {
      func(...args);
    }, delay);
  };
}

export function isNil(value: any): boolean {
  return value === null || value === undefined;
}

export function upperFirst(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function isEmpty(val: string | string[]): boolean {
  return !val || val.length === 0;
}

export function escapeRegExp(text: string | null | undefined): string {
  if (text == null || typeof text !== 'string') {
    return '';
  }
  return text.replace(/[.*+?^${}()|[\]\\,]/ig, '\\$&');
}

/**
 * Regular expression for matching latitude and longitude coordinates format.
*/
const LATLNG_DD_FORMAT: RegExp = new RegExp([
  `^[-+]?([1-8]?\\d(\\.\\d+)?|90(\\.0+)?)\\s*,\\s*[-+]?(180(\\.0+)?`,
  `|((1[0-7]\\d)|([1-9]?\\d))(\\.\\d+)?)$`,
].join(''));

const LATLNG_DMS_FORMAT: RegExp = new RegExp([
  `([0-9]{1,2})\\s*[:|°]\\s*([0-9]{1,2})\\s*[:|'|′]\\s*?([0-9]{1,2}(?:\\.[0-9]+){0,1})\\s*?`,
  `["|″]\\s*([N|S])\\s*([0-9]{1,3})\\s*[:|°]\\s*([0-9]{1,2})\\s*[:|'|′]\\s*?([0-9]{1,2}(?:\\.[0-9]+){0,1})\\s*?["|″]\\s*([E|W])`,
].join(''));

const LATLNG_DMM_FORMAT: RegExp = new RegExp([
  `^([-+]?)([\\d]{1,2})(((\\s)([0-9]{1,2}(?:\\.[0-9]+){0,1})(\\s*)(,)))(\\s*)([-+]?)`,
  `([\\d]{1,2})(((\\s)([0-9]{1,2}(?:\\.[0-9]+){0,1})(\\s*)))`,
].join(''));

export function isCoodinatesInput(input: string): boolean {
  return (
    LATLNG_DD_FORMAT.test(input) || LATLNG_DMM_FORMAT.test(input) || LATLNG_DMS_FORMAT.test(input)
  );
}

/**
 * A simple memoize function that caches the results of expensive function calls
 * and returns the cached result when the same inputs occur again.
 *
 * @param fn The function to memoize
 * @param getKey A function that generates a cache key for the arguments
 * @returns A memoized version of the input function
 */
export function memoize<T extends (...args: any[]) => any>(
  fn: T,
  getKey: (...args: Parameters<T>) => string = (...args) => JSON.stringify(args)
): T {
  const cache = new Map<string, ReturnType<T>>();

  return function (this: any, ...args: Parameters<T>): ReturnType<T> {
    const key = getKey(...args);
    if (cache.has(key)) {
      return cache.get(key)!;
    }
    const result = fn.apply(this, args);
    cache.set(key, result);
    return result;
  } as T;
}

// Example usage:
// const memoizedFilterSuggestions = memoize(
//   (query: string, suggestions: PhxSuggestions) => filterSuggestionsImpl(query, suggestions),
//   (query, suggestions) => query + JSON.stringify(suggestions)
// );

/**
 * Compares values, focusing only on the 'value' property for objects
 * Handles primitive values, arrays of values, and objects with 'value' property
 * 
 * @param val1 First value to compare
 * @param val2 Second value to compare
 * @returns boolean indicating if the values are equal
 */
export function deepCompareValues(val1: any, val2: any): boolean {
  if (Array.isArray(val1)) {
    if (!Array.isArray(val2) || val1.length !== val2.length) return false;

    const values2 = new Set(val2.map(item => (item && typeof item === 'object') ? item.value : item));

    return val1.every(item => values2.has((item && typeof item === 'object') ? item.value : item));
  }

  if (typeof val1 === 'object' && typeof val2 === 'object') {
    return val1.value === val2.value;
  }

  return val1 === val2;
}

/**
 * Converts special characters in a string to their corresponding HTML entities.
 * @param str The input string to convert.
 * @returns The string with HTML entities.
 */
export function escape(str: string): string {
  if (str == null) return '';
  const strValue = typeof str === 'string' ? str : String(str);

  const htmlEntities: { [key: string]: string } = {
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '`': '&#96;',
    '&': '&amp;',
    '/': '&#47;',
    '=': '&#61;',
    '.': '&#46;',
    '*': '&#42;',
    '+': '&#43;',
    '?': '&#63;',
    '^': '&#94;',
    '$': '&#36;',
    '{': '&#123;',
    '}': '&#125;',
    '(': '&#40;',
    ')': '&#41;',
    '|': '&#124;',
    '[': '&#91;',
    ']': '&#93;',
    '\\': '&#92;'
  };

  const specialChars = /[.*+?^${}()|[\]\\<>"'`&=/]/g;

  return strValue.replace(specialChars, (char) => htmlEntities[char] || char);
}


export function generateUuid(): string {
  let d = Date.now();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (d + Math.floor(Math.random() * 16)) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

export function getCookie(name: string): string | null {
  const cookieValue = document.cookie.split('; ').find((row) => row.startsWith(name + '='));

  return cookieValue ? cookieValue.split('=')[1] : null;
}

export function setCookie(name: string, value: string, expiryDays: number = 7): void {
  const date = new Date();
  date.setTime(date.getTime() + expiryDays * 24 * 60 * 60 * 1000);
  const expires = '; expires=' + date.toUTCString();

  document.cookie = name + '=' + value + expires + '; path=/';
}